import React, { useState } from "react";
import { ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTrash,
  faCheckCircle,
  faTimesCircle,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";

import useAdminWhitelist from "../../services/Admin/useAdminWhiteList";
import { RefreshCcw } from "lucide-react";

const AdminWhitelist = ({ Admin }) => {
  const {
    filteredClients,

    searchTerm,
    setSearchTerm,

    loading,
    actionLoading,

    showModal,
    setShowModal,

    name,
    setName,
    mobile,
    setMobile,

    fetchClients,
    addClient,
    deleteClient,
  } = useAdminWhitelist({ Admin });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteClient, setSelectedDeleteClient] = useState(null);

  const openDeleteModal = (client) => {
    setSelectedDeleteClient(client);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedDeleteClient(null);
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDeleteClient?.id) return;

    await deleteClient(selectedDeleteClient.id);
    closeDeleteModal();
  };

  return (
    <div className="min-h-screen bg-[#dfedff36]">
      <ToastContainer />

      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-gray-200 bg-white px-5 py-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-[34px] text-green-600"
          />

          <h1 className="text-3xl font-semibold text-gray-500">
            Whitelisted
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={fetchClients}
            disabled={loading || actionLoading}
            className="flex h-[40px] items-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCcw className="h-5 w-5 text-gray-600" />
            Refresh
          </button>

          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="flex h-[40px] items-center gap-2 rounded-md bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Whitelist
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Search */}
        <div className="mb-4 flex">
          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-[42px] w-full rounded-md border border-gray-300 bg-white pl-10 pr-4 text-[15px] font-medium text-gray-800 outline-none transition placeholder:font-normal placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full min-w-[1100px] border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-700">
                <th className="whitespace-nowrap border-b px-4 py-3 font-bold">
                  ID
                </th>
                <th className="whitespace-nowrap border-b px-4 py-3 font-bold">
                  Code
                </th>
                <th className="whitespace-nowrap border-b px-4 py-3 font-bold">
                  User Name
                </th>
                <th className="whitespace-nowrap border-b px-4 py-3 font-bold">
                  Name
                </th>
                <th className="whitespace-nowrap border-b px-4 py-3 font-bold">
                  Mobile
                </th>
                <th className="whitespace-nowrap border-b px-4 py-3 font-bold">
                  Status
                </th>
                <th className="whitespace-nowrap border-b px-4 py-3 font-bold">
                  Whitelisted On
                </th>
                <th className="whitespace-nowrap border-b px-4 py-3 font-bold">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <div className="h-6 w-6 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
                      <span className="text-sm font-semibold text-gray-500">
                        Loading clients...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : filteredClients.length > 0 ? (
                filteredClients.map((client) => {
                  const isActive = client.status === "Y";

                  return (
                    <tr
                      key={client.id}
                      className="border-b text-gray-700 hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-4 py-3">
                        {client.id}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        {client.user_code}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        {client.user_name}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 font-semibold text-gray-900">
                        {client.name}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        {client.mobile_number}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${
                            isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={isActive ? faCheckCircle : faTimesCircle}
                          />
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        {client.created_on}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <button
                          type="button"
                          onClick={() => openDeleteModal(client)}
                          disabled={actionLoading}
                          className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-10 text-center text-sm font-semibold text-gray-500"
                  >
                    No whitelist clients found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-[500px] rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-lg font-bold text-gray-800">
                Add Whitelist
              </h3>

              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-md px-2 py-1 text-2xl leading-none text-gray-500 hover:bg-gray-100"
              >
                ×
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">
                  Name
                </label>

                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-[42px] w-full rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">
                  Mobile
                </label>

                <input
                  type="text"
                  value={mobile}
                  onChange={(e) =>
                    setMobile(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  className="h-[42px] w-full rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-md bg-gray-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-600"
              >
                Close
              </button>

              <button
                type="button"
                onClick={addClient}
                disabled={actionLoading}
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {actionLoading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && selectedDeleteClient && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-[520px] rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  Confirm Delete
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Please review the whitelist details before deleting.
                </p>
              </div>

              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={actionLoading}
                className="rounded-md px-2 py-1 text-2xl leading-none text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-5">
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm font-semibold text-red-700">
                  Are you sure you want to delete this whitelist entry?
                </p>
                <p className="mt-1 text-xs text-red-600">
                  This action cannot be undone.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm md:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-400">
                    ID
                  </p>
                  <p className="mt-1 font-semibold text-gray-800">
                    {selectedDeleteClient.id || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-gray-400">
                    Code
                  </p>
                  <p className="mt-1 font-semibold text-gray-800">
                    {selectedDeleteClient.user_code || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-gray-400">
                    User Name
                  </p>
                  <p className="mt-1 font-semibold text-gray-800">
                    {selectedDeleteClient.user_name || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-gray-400">
                    Name
                  </p>
                  <p className="mt-1 font-semibold text-gray-800">
                    {selectedDeleteClient.name || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-gray-400">
                    Mobile
                  </p>
                  <p className="mt-1 font-semibold text-gray-800">
                    {selectedDeleteClient.mobile_number || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-gray-400">
                    Status
                  </p>
                  <span
                    className={`mt-1 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${
                      selectedDeleteClient.status === "Y"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={
                        selectedDeleteClient.status === "Y"
                          ? faCheckCircle
                          : faTimesCircle
                      }
                    />
                    {selectedDeleteClient.status === "Y"
                      ? "Active"
                      : "Inactive"}
                  </span>
                </div>

                <div className="md:col-span-2">
                  <p className="text-xs font-semibold uppercase text-gray-400">
                    Whitelisted On
                  </p>
                  <p className="mt-1 font-semibold text-gray-800">
                    {selectedDeleteClient.created_on || "-"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={actionLoading}
                className="rounded-md bg-gray-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FontAwesomeIcon icon={faTrash} />
                {actionLoading ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWhitelist;